/**
 * Vera Ideas — shared UI helpers: status display mapping, rotating prompt
 * copy, relative time, month-granularity dates, and safe plain-text rendering
 * (line breaks preserved, URLs auto-linked, everything else inert).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Badge } from '@fluentui/react-components';
import type { IdeaStatus } from '../api/ideas';

// ---------------------------------------------------------------------------
// Status display mapping — "not_planned" always reads as "Not right now",
// never red: the module is positive-reinforcement only.
// ---------------------------------------------------------------------------

type BadgeColor = 'brand' | 'danger' | 'important' | 'informative' | 'severe' | 'subtle' | 'success' | 'warning';

export const STATUS_META: Record<IdeaStatus, { label: string; color: BadgeColor; outline?: boolean }> = {
  new: { label: 'New', color: 'subtle' },
  under_review: { label: 'Under review', color: 'informative' },
  planned: { label: 'Planned', color: 'brand' },
  in_progress: { label: 'In progress', color: 'warning' },
  shipped: { label: 'Shipped 🎉', color: 'success' },
  // informative, not subtle: this is the only outline pill here, and Fluent's
  // outline-subtle is white text (a token for badges over dark imagery), which
  // rendered "Not right now" as an empty ring on the page.
  not_planned: { label: 'Not right now', color: 'informative', outline: true },
};

export function StatusPill({ status }: { status: IdeaStatus }) {
  const meta = STATUS_META[status];
  return (
    <Badge
      appearance={meta.outline ? 'outline' : 'tint'}
      color={meta.color}
      size="medium"
      shape="rounded"
    >
      {meta.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Rotating prompt copy — the submission ask is never a blank "Submit an idea".
// ---------------------------------------------------------------------------

export const IDEA_PROMPTS = [
  'What would make your day easier?',
  'What should Vera do next?',
  'Got a spark of an idea? Big or small, we want it.',
  'Seen something clunky? Tell us — we love fixing clunky.',
  'If you had a magic wand for one workflow, which one?',
] as const;

/** Cycle through items with a brief opacity dip between swaps. */
export function useRotating<T>(items: readonly T[], intervalMs = 8000): { item: T; visible: boolean } {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % items.length);
        setVisible(true);
      }, 300);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [items.length, intervalMs]);

  return { item: items[index % items.length], visible };
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? 'yesterday' : `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Planned for July 2026" — month granularity even though a full date is stored. */
export function monthLabel(isoDate: string): string {
  const [y, m] = isoDate.slice(0, 10).split('-').map(Number);
  if (!y || !m) return '';
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Safe plain-text rendering
// ---------------------------------------------------------------------------

const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/g;

/**
 * Render user text safely: React escapes markup (a `<script>` body shows as
 * literal text), line breaks are preserved, and bare http(s) URLs become
 * links that open in a new tab.
 */
export function SafeText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  const pattern = new RegExp(URL_PATTERN.source, 'g');
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const url = match[0];
    parts.push(
      <a key={`${match.index}-${url}`} href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </a>
    );
    last = match.index + url.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{parts}</span>;
}

// ---------------------------------------------------------------------------
// Notification copy
// ---------------------------------------------------------------------------

export function notificationText(kind: string, payload: Record<string, unknown>): string {
  const title = typeof payload.title === 'string' ? payload.title : 'your idea';
  switch (kind) {
    case 'idea_shipped': {
      const firstName = typeof payload.firstName === 'string' && payload.firstName ? `, ${payload.firstName}` : '';
      return `🎉 "${title}" just shipped — thanks for the idea${firstName}!`;
    }
    case 'new_comment': {
      const who = typeof payload.commenterName === 'string' ? payload.commenterName : 'Someone';
      return `${who} commented on "${title}"`;
    }
    case 'idea_merged':
      return `Your idea joined forces with "${title}" — your vote came along!`;
    case 'status_change': {
      const to = typeof payload.toStatus === 'string' ? (payload.toStatus as IdeaStatus) : null;
      const label = to && STATUS_META[to] ? STATUS_META[to].label : 'a new status';
      return `"${title}" moved to ${label}`;
    }
    default:
      return `Update on "${title}"`;
  }
}
